import { useMutation, useQuery, useQueryClient, queryOptions } from "@tanstack/react-query";
import { parseListResponse, parseResponse } from "lib/fetch";
import type { CollaboratorInterface, CompanyLimitInterface } from "types/collaborator";
import {
    getCollaboratorRequest,
    getCollaboratorsRequest,
    getCollaboratorCompanyLimitRequest,
    updateCollaboratorRequest,
} from "lib/api/collaborator";

export const fetchCollaborators = (params?: HashMap<unknown>) =>
    parseListResponse<CollaboratorInterface>(getCollaboratorsRequest(params));

export const fetchCollaborator = (id: string) =>
    parseResponse<CollaboratorInterface>(getCollaboratorRequest(id));

export const updateCollaborator = (id: string, data: HashMap<unknown>) =>
    parseResponse<CollaboratorInterface>(updateCollaboratorRequest(id, data));

export const fetchCollaboratorCompanyLimit = (id: string) =>
    parseResponse<CompanyLimitInterface>(getCollaboratorCompanyLimitRequest(id));

export const collaboratorKeys = {
    all: ["collaborator"] as const,
    lists: () => [...collaboratorKeys.all, "list"] as const,
    details: () => [...collaboratorKeys.all, "detail"] as const,
    detail: (id: string) => [...collaboratorKeys.details(), id] as const,
    companyLimit: (id: string) => [...collaboratorKeys.all, "company_limit", id] as const,
};

export const collaboratorOptions = (id: string | undefined) =>
    queryOptions({
        queryKey: id ? collaboratorKeys.detail(id) : collaboratorKeys.details(),
        queryFn: () => (id ? fetchCollaborator(id) : Promise.reject(new Error("Missing id"))),
        enabled: Boolean(id),
    });

export const collaboratorCompanyLimitOptions = (id: string | undefined) =>
    queryOptions({
        queryKey: id ? collaboratorKeys.companyLimit(id) : [...collaboratorKeys.all, "company_limit"],
        queryFn: () => (id ? fetchCollaboratorCompanyLimit(id) : Promise.reject(new Error("Missing id"))),
        enabled: Boolean(id),
    });

export const useCollaborator = (id: string | undefined) => useQuery(collaboratorOptions(id));
export const useCollaboratorCompanyLimit = (id: string | undefined) => useQuery(collaboratorCompanyLimitOptions(id));

export const useUpdateCollaborator = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (variables: { id: string; data: HashMap<unknown> }) =>
            updateCollaborator(variables.id, variables.data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: collaboratorKeys.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: collaboratorKeys.lists() });
        },
    });
};
